import Decimal from 'decimal.js';
import { groupBy } from 'lodash';
import { useMemo } from 'react';

import { SaleLoanItemsTableProps } from '../StrategyItemsTable';
import {
  createCharitableTrustBeneficiaryPaymentTableItem,
  createGratAnnuityTableItem,
  createGratBeneficiaryPaymentTableItem,
  createGratTableItem,
  createHypotheticalCharitableTrustIncomePaymentTableItem,
  createHypotheticalCltTableItem,
  createHypotheticalCrtTableItem,
  createHypotheticalInsuranceTableItem,
  createQprtBeneficiaryPaymentTableItem,
  createSaleLoanTableFundingItem,
  createSaleLoanTableRepaymentItem,
} from '../StrategyItemsTable.utils';
import { TransferTab } from '../TransferItems';

export interface StrategyItemsTableIncomePaymentItem {
  item: {
    id: string;
    heading: string;
    description: string;
    context: {
      heading: string;
      headingSx?: {
        color: string;
      };
      subheading: string;
    };
  };
  value: Decimal;
}

export function useStrategyItems(props: SaleLoanItemsTableProps) {
  // each loan should be represented as one outbound item and one inbound item.
  // the outbound item represents the initial sale or loan, and the inbound item
  // represents the repayment(s) of the loan.

  const {
    direction,
    saleLoansWithProjections,
    grats,
    gratAnnuities,
    gratBeneficiaryPayments,
    hypotheticalCrts,
    hypotheticalCrtIncomePayments,
    hypotheticalCrtBeneficiaryPayments,
    hypotheticalClts,
    hypotheticalCltIncomePayments,
    hypotheticalCltBeneficiaryPayments,
    hypotheticalInsurance,
    qprtBeneficiaryPayments,
    transferLog,
    onEditStrategyTransfer,
  } = props;

  const strategyItems = useMemo(() => {
    if (direction === TransferTab.Outbound) {
      const sourceItemsAndValues = saleLoansWithProjections.sourceLoans.map(
        ({ saleLoan, loanProjections }) =>
          createSaleLoanTableFundingItem(
            saleLoan,
            loanProjections,
            {
              heading: 'recipient',
              direction: direction,
            },
            onEditStrategyTransfer
          )
      );
      const sourceItemsAndValuesGrats = grats.sourceGrats.map((grat) =>
        createGratTableItem(
          grat,
          {
            direction: direction,
          },
          onEditStrategyTransfer,
          transferLog
        )
      );
      const sourceItemsAndValuesCrts = hypotheticalCrts.sourceCrts.map(
        (hypotheticalCrt) =>
          createHypotheticalCrtTableItem(
            hypotheticalCrt,
            {
              direction: direction,
            },
            onEditStrategyTransfer,
            transferLog
          )
      );
      const sourceItemsAndValuesClts = hypotheticalClts.sourceClts.map(
        (hypotheticalClt) =>
          createHypotheticalCltTableItem(
            hypotheticalClt,
            {
              direction: direction,
            },
            onEditStrategyTransfer,
            transferLog
          )
      );

      const sourceItemsAndValuesInsurance =
        hypotheticalInsurance.sourceInsurance.map((insurance) =>
          createHypotheticalInsuranceTableItem(
            insurance,
            {
              direction: direction,
            },
            onEditStrategyTransfer,
            transferLog
          )
        );

      const sourceItemsAndValuesAnnuities = [];

      const gratAnnuityItem = createGratAnnuityTableItem(gratAnnuities, {
        direction,
      });

      if (gratAnnuityItem) {
        sourceItemsAndValuesAnnuities.push(gratAnnuityItem);
      }

      const sourceItemsAndValuesCrtIncomePayments: StrategyItemsTableIncomePaymentItem[] =
        [];

      const sourceCrtIncomePayments = groupBy(
        hypotheticalCrtIncomePayments,
        (crtIncomePayment) => crtIncomePayment.destination?.node?.id
      );

      Object.values(sourceCrtIncomePayments).forEach((crtIncomePayments) => {
        const sourceCrtIncomePaymentItem =
          createHypotheticalCharitableTrustIncomePaymentTableItem(
            crtIncomePayments,
            {
              direction,
            }
          );

        if (sourceCrtIncomePaymentItem) {
          sourceItemsAndValuesCrtIncomePayments.push(
            sourceCrtIncomePaymentItem
          );
        }
      });

      const sourceItemsAndValuesCltIncomePayments: StrategyItemsTableIncomePaymentItem[] =
        [];

      const sourceCltIncomePayments = groupBy(
        hypotheticalCltIncomePayments,
        (cltIncomePayment) => cltIncomePayment.destination?.node?.id
      );

      Object.values(sourceCltIncomePayments).forEach((cltIncomePayments) => {
        const sourceCltIncomePaymentItem =
          createHypotheticalCharitableTrustIncomePaymentTableItem(
            cltIncomePayments,
            {
              direction,
            }
          );

        if (sourceCltIncomePaymentItem) {
          sourceItemsAndValuesCltIncomePayments.push(
            sourceCltIncomePaymentItem
          );
        }
      });

      const sourceItemsAndValuesBeneficiaryPayments = [];

      const sourceBeneficiaryPaymentItem =
        createGratBeneficiaryPaymentTableItem(gratBeneficiaryPayments, {
          direction,
        });

      if (sourceBeneficiaryPaymentItem) {
        sourceItemsAndValuesBeneficiaryPayments.push(
          sourceBeneficiaryPaymentItem
        );
      }

      const sourceQprtBeneficiaryPaymentItem =
        createQprtBeneficiaryPaymentTableItem(qprtBeneficiaryPayments, {
          direction,
        });

      if (sourceQprtBeneficiaryPaymentItem) {
        sourceItemsAndValuesBeneficiaryPayments.push(
          sourceQprtBeneficiaryPaymentItem
        );
      }

      const sourceItemsAndValuesCrtBeneficiaryPayments: StrategyItemsTableIncomePaymentItem[] =
        [];

      const sourceCrtBeneficiaryPayments = groupBy(
        hypotheticalCrtBeneficiaryPayments,
        (crtBeneficiaryPayment) => crtBeneficiaryPayment.destination?.node?.id
      );

      Object.values(sourceCrtBeneficiaryPayments).forEach(
        (crtBeneficiaryPayments) => {
          const sourceCrtBeneficiaryPaymentItem =
            createCharitableTrustBeneficiaryPaymentTableItem(
              crtBeneficiaryPayments,
              {
                direction,
              }
            );

          if (sourceCrtBeneficiaryPaymentItem) {
            sourceItemsAndValuesCrtBeneficiaryPayments.push(
              sourceCrtBeneficiaryPaymentItem
            );
          }
        }
      );

      const sourceItemsAndValuesCltBeneficiaryPayments: StrategyItemsTableIncomePaymentItem[] =
        [];

      const sourceCltBeneficiaryPayments = groupBy(
        hypotheticalCltBeneficiaryPayments,
        (cltBeneficiaryPayment) => cltBeneficiaryPayment.destination?.node?.id
      );

      Object.values(sourceCltBeneficiaryPayments).forEach(
        (cltBeneficiaryPayments) => {
          const sourceCltBeneficiaryPaymentItem =
            createCharitableTrustBeneficiaryPaymentTableItem(
              cltBeneficiaryPayments,
              {
                direction,
              }
            );

          if (sourceCltBeneficiaryPaymentItem) {
            sourceItemsAndValuesCltBeneficiaryPayments.push(
              sourceCltBeneficiaryPaymentItem
            );
          }
        }
      );

      const recipientItemsAndValues =
        saleLoansWithProjections.recipientLoans.map(
          ({ saleLoan, loanProjections }) =>
            createSaleLoanTableRepaymentItem(
              saleLoan,
              loanProjections,
              {
                heading: 'source',
                direction: direction,
              },
              onEditStrategyTransfer
            )
        );
      return [
        ...sourceItemsAndValues,
        ...sourceItemsAndValuesGrats,
        ...sourceItemsAndValuesCrts,
        ...sourceItemsAndValuesClts,
        ...sourceItemsAndValuesInsurance,
        ...sourceItemsAndValuesAnnuities,
        ...sourceItemsAndValuesCrtIncomePayments,
        ...sourceItemsAndValuesCltIncomePayments,
        ...sourceItemsAndValuesBeneficiaryPayments,
        ...sourceItemsAndValuesCrtBeneficiaryPayments,
        ...sourceItemsAndValuesCltBeneficiaryPayments,
        ...recipientItemsAndValues,
      ];
    } else {
      const recipientItemsAndValues =
        saleLoansWithProjections.recipientLoans.map(
          ({ saleLoan, loanProjections }) =>
            createSaleLoanTableFundingItem(
              saleLoan,
              loanProjections,
              {
                heading: 'source',
                direction: direction,
              },
              onEditStrategyTransfer
            )
        );
      const recipientItemsAndValuesGrats = grats.recipientGrats.map((grat) =>
        createGratTableItem(
          grat,
          {
            direction: direction,
          },
          onEditStrategyTransfer,
          transferLog
        )
      );

      const recipientItemsAndValuesCrts = hypotheticalCrts.recipientCrts.map(
        (hypotheticalCrt) =>
          createHypotheticalCrtTableItem(
            hypotheticalCrt,
            {
              direction: direction,
            },
            onEditStrategyTransfer,
            transferLog
          )
      );

      const recipientItemsAndValuesClts = hypotheticalClts.recipientClts.map(
        (hypotheticalClt) =>
          createHypotheticalCltTableItem(
            hypotheticalClt,
            {
              direction: direction,
            },
            onEditStrategyTransfer,
            transferLog
          )
      );

      const recipientItemsAndValuesInsurance =
        hypotheticalInsurance.recipientInsurance.map((insurance) =>
          createHypotheticalInsuranceTableItem(
            insurance,
            {
              direction: direction,
            },
            onEditStrategyTransfer,
            transferLog
          )
        );

      const recipientItemsAndValuesAnnuities = [];

      const gratAnnuityItem = createGratAnnuityTableItem(gratAnnuities, {
        direction,
      });

      if (gratAnnuityItem) {
        recipientItemsAndValuesAnnuities.push(gratAnnuityItem);
      }

      const recipientItemsAndValuesCrtIncomePayments: StrategyItemsTableIncomePaymentItem[] =
        [];

      const recipientCrtIncomePayments = groupBy(
        hypotheticalCrtIncomePayments,
        (crtIncomePayment) => crtIncomePayment.destination?.node?.id
      );

      Object.values(recipientCrtIncomePayments).forEach((crtIncomePayments) => {
        const recipientCrtIncomePaymentItem =
          createHypotheticalCharitableTrustIncomePaymentTableItem(
            crtIncomePayments,
            {
              direction,
            }
          );

        if (recipientCrtIncomePaymentItem) {
          recipientItemsAndValuesCrtIncomePayments.push(
            recipientCrtIncomePaymentItem
          );
        }
      });

      const recipientItemsAndValuesCltIncomePayments: StrategyItemsTableIncomePaymentItem[] =
        [];

      const recipientCltIncomePayments = groupBy(
        hypotheticalCltIncomePayments,
        (cltIncomePayment) => cltIncomePayment.destination?.node?.id
      );

      Object.values(recipientCltIncomePayments).forEach((cltIncomePayments) => {
        const recipientCltIncomePaymentItem =
          createHypotheticalCharitableTrustIncomePaymentTableItem(
            cltIncomePayments,
            {
              direction,
            }
          );

        if (recipientCltIncomePaymentItem) {
          recipientItemsAndValuesCltIncomePayments.push(
            recipientCltIncomePaymentItem
          );
        }
      });

      const recipientItemsAndValuesBeneficiaryPayments = [];

      const recipientBeneficiaryPaymentItem =
        createGratBeneficiaryPaymentTableItem(gratBeneficiaryPayments, {
          direction,
        });

      if (recipientBeneficiaryPaymentItem) {
        recipientItemsAndValuesBeneficiaryPayments.push(
          recipientBeneficiaryPaymentItem
        );
      }

      const recipientQprtBeneficiaryPaymentItem =
        createQprtBeneficiaryPaymentTableItem(qprtBeneficiaryPayments, {
          direction,
        });

      if (recipientQprtBeneficiaryPaymentItem) {
        recipientItemsAndValuesBeneficiaryPayments.push(
          recipientQprtBeneficiaryPaymentItem
        );
      }

      const recipientItemsAndValuesCrtBeneficiaryPayments: StrategyItemsTableIncomePaymentItem[] =
        [];

      const recipientCrtBeneficiaryPayments = groupBy(
        hypotheticalCrtBeneficiaryPayments,
        (crtBeneficiaryPayment) => crtBeneficiaryPayment.destination?.node?.id
      );

      Object.values(recipientCrtBeneficiaryPayments).forEach(
        (crtBeneficiaryPayments) => {
          const recipientCrtBeneficiaryPaymentItem =
            createCharitableTrustBeneficiaryPaymentTableItem(
              crtBeneficiaryPayments,
              {
                direction,
              }
            );

          if (recipientCrtBeneficiaryPaymentItem) {
            recipientItemsAndValuesCrtBeneficiaryPayments.push(
              recipientCrtBeneficiaryPaymentItem
            );
          }
        }
      );

      const recipientItemsAndValuesCltBeneficiaryPayments: StrategyItemsTableIncomePaymentItem[] =
        [];

      const recipientCltBeneficiaryPayments = groupBy(
        hypotheticalCltBeneficiaryPayments,
        (cltBeneficiaryPayment) => cltBeneficiaryPayment.destination?.node?.id
      );

      Object.values(recipientCltBeneficiaryPayments).forEach(
        (cltBeneficiaryPayments) => {
          const recipientCltBeneficiaryPaymentItem =
            createCharitableTrustBeneficiaryPaymentTableItem(
              cltBeneficiaryPayments,
              {
                direction,
              }
            );

          if (recipientCltBeneficiaryPaymentItem) {
            recipientItemsAndValuesCltBeneficiaryPayments.push(
              recipientCltBeneficiaryPaymentItem
            );
          }
        }
      );

      const sourceItemsAndValues = saleLoansWithProjections.sourceLoans.map(
        ({ saleLoan, loanProjections }) =>
          createSaleLoanTableRepaymentItem(
            saleLoan,
            loanProjections,
            {
              heading: 'recipient',
              direction: direction,
            },
            onEditStrategyTransfer
          )
      );
      return [
        ...recipientItemsAndValues,
        ...recipientItemsAndValuesGrats,
        ...recipientItemsAndValuesCrts,
        ...recipientItemsAndValuesClts,
        ...recipientItemsAndValuesInsurance,
        ...recipientItemsAndValuesAnnuities,
        ...recipientItemsAndValuesCrtIncomePayments,
        ...recipientItemsAndValuesCltIncomePayments,
        ...recipientItemsAndValuesBeneficiaryPayments,
        ...recipientItemsAndValuesCrtBeneficiaryPayments,
        ...recipientItemsAndValuesCltBeneficiaryPayments,
        ...sourceItemsAndValues,
      ];
    }
  }, [
    direction,
    gratAnnuities,
    gratBeneficiaryPayments,
    grats.recipientGrats,
    grats.sourceGrats,
    hypotheticalCltBeneficiaryPayments,
    hypotheticalCltIncomePayments,
    hypotheticalClts.recipientClts,
    hypotheticalClts.sourceClts,
    hypotheticalCrtBeneficiaryPayments,
    hypotheticalCrtIncomePayments,
    hypotheticalCrts.recipientCrts,
    hypotheticalCrts.sourceCrts,
    hypotheticalInsurance,
    onEditStrategyTransfer,
    qprtBeneficiaryPayments,
    saleLoansWithProjections.recipientLoans,
    saleLoansWithProjections.sourceLoans,
    transferLog,
  ]);

  return strategyItems;
}
